import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, ChevronRight } from "lucide-react";

const CONSENT_KEY = "vetassist_analytics_consent";

/**
 * CookieConsentBanner
 *
 * A slide-up banner that asks users for analytics consent.
 * - "Accept All" → triggers window.vetAssistAnalytics.grantConsent()
 * - "Reject All" → sets consent to denied, hides banner permanently
 * - Respects existing consent state from localStorage
 */
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Check if user has already made a consent choice
    try {
      const stored = window.localStorage.getItem(CONSENT_KEY);
      if (stored === "granted" || stored === "denied") {
        setVisible(false);
        return;
      }
    } catch {
      // localStorage unavailable — show banner anyway
    }

    // Delay initial appearance so page content loads first
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    setIsExiting(true);
    try {
      if (window.vetAssistAnalytics?.grantConsent) {
        window.vetAssistAnalytics.grantConsent();
      } else {
        // Fallback: manually set localStorage if analytics API is unavailable
        window.localStorage.setItem(CONSENT_KEY, "granted");
      }
    } catch {
      // Silently fail
    }
    setTimeout(() => {
      setVisible(false);
      setDismissed(true);
    }, 400);
  };

  const handleReject = () => {
    setIsExiting(true);
    try {
      window.localStorage.setItem(CONSENT_KEY, "denied");
      if (window.vetAssistAnalytics?.revokeConsent) {
        window.vetAssistAnalytics.revokeConsent();
      }
    } catch {
      // Silently fail
    }
    setTimeout(() => {
      setVisible(false);
      setDismissed(true);
    }, 400);
  };

  return (
    <AnimatePresence>
      {visible && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed inset-x-0 bottom-0 z-9999 p-3 sm:p-4 md:p-6"
        >
          <motion.div
            animate={isExiting ? { scale: 0.97, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-blue-900/10 backdrop-blur-xl"
          >
            {/* Subtle top gradient accent */}
            <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-blue-500 via-emerald-400 to-blue-500" />

            <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:gap-5 sm:p-6 md:p-8">
              {/* Icon */}
              <div className="hidden shrink-0 sm:flex sm:h-12 sm:w-12 md:h-14 md:w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-100">
                <Cookie className="h-6 w-6 text-[#2546A1] md:h-7 md:w-7" />
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900 sm:text-lg">
                  <span className="sm:hidden mr-2">🍪</span>
                  We value your privacy
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 sm:text-base sm:leading-relaxed">
                  We use analytics cookies to understand how you use our site so we can
                  improve VetAssist for you. You can choose to accept or reject these.
                  Read our{" "}
                  <Link
                    to="/privacy"
                    className="inline-flex items-center gap-0.5 font-semibold text-[#2546A1] underline decoration-[#2546A1]/30 underline-offset-2 transition-colors hover:decoration-[#2546A1]"
                  >
                    Privacy Policy
                    <ChevronRight className="h-3 w-3" />
                  </Link>{" "}
                  for more details.
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6 md:px-8">
              {/* Privacy link (mobile) */}
              <Link
                to="/privacy"
                className="mb-1 mr-auto flex items-center gap-1 text-xs font-medium text-slate-500 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-slate-700 sm:hidden"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Privacy Policy
              </Link>

              <button
                onClick={handleReject}
                className="order-3 cursor-pointer rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-xs transition-all hover:bg-slate-100 hover:border-slate-400 active:scale-[0.98] sm:order-1 sm:px-6 sm:py-2.5"
              >
                Reject All
              </button>

              <button
                onClick={handleAccept}
                className="order-4 cursor-pointer rounded-xl bg-gradient-to-r from-[#2546A1] to-[#1d3782] px-6 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-900/20 transition-all hover:from-[#1d3782] hover:to-[#162b63] hover:shadow-lg hover:shadow-blue-900/30 active:scale-[0.98] sm:px-8 sm:py-2.5"
              >
                Accept All
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
